import {
  format,
  getDate,
  getHours,
  getMilliseconds,
  getMinutes,
  getMonth,
  getSeconds,
  getYear,
  isValid,
  parseISO,
} from 'date-fns';
import { trunc } from '@trinity/chat-components/utils';
import { abbreviateNumber, extractUnit } from '@trinity/chat-components/utils/chart';

export type FormatAxisLabelOptions = {
  value: string | number | null | undefined;
  unit?: string;
  isPrefix?: boolean;
  /**
   * Full set of tick values on this axis. When provided and the values are ISO date strings,
   * enables context-aware date formatting that picks the coarsest granularity that keeps
   * ticks distinct (e.g. drops "T00:00:00.000" when every tick is at midnight).
   */
  allValues?: readonly (string | number | null | undefined)[];
};

function coerceToFiniteNumber(value: string | number): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = value.trim();
  if (trimmed === '') return null;
  // Number() (unlike parseFloat) rejects trailing garbage like "2025-07-01" → NaN,
  // so ISO dates and numeric-prefixed strings stay categorical.
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

// Parse "$1,450,000" / "12.4%" → { number, unit, isPrefix }. Returns null if the
// non-unit portion doesn't cleanly parse as a number.
function parseValueWithUnit(value: string): { number: number; unit: string; isPrefix: boolean } | null {
  const detected = extractUnit(value);
  if (!detected) return null;
  const stripped = (
    detected.isPrefix
      ? value.trim().slice(detected.unit.length)
      : value.trim().slice(0, -detected.unit.length)
  )
    .trim()
    .replace(/,/g, '');
  const n = Number(stripped);
  return Number.isFinite(n) ? { number: n, unit: detected.unit, isPrefix: detected.isPrefix } : null;
}

// Gate on YYYY-MM-DD… shape before handing to date-fns, so bare numbers like "2025"
// (which parseISO would happily accept as Jan 1) don't get classified as dates.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

function parseIsoDate(value: string): Date | null {
  if (!ISO_DATE_RE.test(value)) return null;
  const d = parseISO(value);
  return isValid(d) ? d : null;
}

type DateGranularity = 'datetime' | 'day' | 'month' | 'year';

function pickDateGranularity(dates: readonly Date[]): DateGranularity {
  if (dates.length === 0) return 'day';

  const allMidnight = dates.every(
    (d) => getHours(d) === 0 && getMinutes(d) === 0 && getSeconds(d) === 0 && getMilliseconds(d) === 0
  );
  if (!allMidnight) return 'datetime';

  const allFirstOfMonth = dates.every((d) => getDate(d) === 1);
  if (!allFirstOfMonth) return 'day';

  const allJanuary = dates.every((d) => getMonth(d) === 0);
  if (allJanuary) return 'year';

  return 'month';
}

function formatDate(date: Date, granularity: DateGranularity, includeYear: boolean): string {
  switch (granularity) {
    case 'year':
      return format(date, 'yyyy');
    case 'month':
      return format(date, includeYear ? 'MMM yyyy' : 'MMM');
    case 'day':
      return format(date, includeYear ? 'MMM d, yyyy' : 'MMM d');
    case 'datetime':
    default:
      return format(date, includeYear ? 'MMM d, yyyy HH:mm' : 'MMM d HH:mm');
  }
}

function tryFormatAsDate(
  value: string,
  allValues: readonly (string | number | null | undefined)[]
): string | null {
  const target = parseIsoDate(value);
  if (!target) return null;

  const peers: Date[] = [];
  for (const v of allValues) {
    if (typeof v !== 'string') continue;
    const d = parseIsoDate(v);
    if (d) peers.push(d);
  }
  if (peers.length === 0) peers.push(target);

  const granularity = pickDateGranularity(peers);
  const years = new Set(peers.map((d) => getYear(d)));
  // Include the year on each tick when the range spans multiple years, so ticks stay unambiguous.
  const includeYear = granularity !== 'year' && years.size > 1;

  return formatDate(target, granularity, includeYear);
}

export function formatAxisLabel({ value, unit, isPrefix, allValues }: FormatAxisLabelOptions): string {
  if (value === null || value === undefined) return '';

  let formattedValue: string;
  let effectiveUnit = unit;
  let effectiveIsPrefix = isPrefix;

  const numeric = coerceToFiniteNumber(value);

  if (numeric !== null) {
    // Numeric axis tick — abbreviate by default (1450000 → 1.45M).
    formattedValue = abbreviateNumber(numeric);
  } else if (typeof value === 'string') {
    // Try currency/percent embedded in the value (e.g. "$1,450,000" → "$1.45M", "12.4%" → "12.4%").
    // The value's own unit takes precedence over any caller-supplied unit.
    const withUnit = parseValueWithUnit(value);
    if (withUnit) {
      formattedValue = abbreviateNumber(withUnit.number);
      effectiveUnit = withUnit.unit;
      effectiveIsPrefix = withUnit.isPrefix;
    } else if (allValues) {
      // Category axis with peer context — try date-aware formatting.
      const asDate = tryFormatAsDate(value, allValues);
      formattedValue = asDate ?? trunc(value, 10);
    } else {
      // Category label — truncate long strings.
      formattedValue = trunc(value, 10);
    }
  } else {
    formattedValue = trunc(String(value), 10);
  }

  if (!effectiveUnit) return formattedValue;
  return effectiveIsPrefix ? `${effectiveUnit}${formattedValue}` : `${formattedValue}${effectiveUnit}`;
}
